/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React, { useCallback, useEffect } from 'react'
import { Button, Icon, Tab } from 'semantic-ui-react'
import { Route, NavLink, Switch, useHistory, Redirect } from 'react-router-dom'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import useBreadcrumb from '../NavBar/useBreadcrumb'
import Revenues from './Revenues'
import Failures from './Failures'
import Detailed from './Detailed'
import useUser from '../Shared/UserProvider/useUser'
import Deployments from './Deployments'
import { useQuerySearchParams } from '../../services/Filter/filter.service'
import PausedStores from './PausedStores'
import { isProviderACompany, isProviderAPos } from '../../services/Provider/Provider.service'
import {
    PerformanceReportConfigurationList,
} from '../PerformanceReportConfiguration/PerformanceReportConfigurationList'
import Margin from './Margin/Margin'
import useFeature from '../Shared/FeatureProvider/useFeature'
import { UnavailableItems } from './UnavailableItems/UnavailableItems'
import { OpeningTimes } from './OpeningTimes/OpeningTimes'
import { OpeningActivity } from './OpeningActivity'
import { FeatureInfo } from '../Shared/FeatureInfo/FeatureInfo'
import { Link } from 'react-router-dom-v5-compat'
import useTracking from '../../services/Tracking/useTracking'
import { useLocalStorageItem } from '../../helpers/useLocalStorageItem'

const defaultFilters = {
    dateRange: {
        startDate: moment().local().add(-7, 'day').startOf('day'),
        endDate: moment().local().add(-1, 'day').endOf('day'),
    },
    dateComparison: {
        startDate: null,
        endDate: null,
    },
    merchantId: null,
    brandIds: [],
    platformIds: [],
}

const Home = () => {
    const [t] = useTranslation()
    const {setPaths} = useBreadcrumb()
    const {user} = useUser()
    const {isEnabled} = useFeature()
    const {track} = useTracking('dashboard')
    const history = useHistory()
    const [, setDashboardSoftRolloutTarget] = useLocalStorageItem('dashboard_soft_rollout_target')

    const query = useQuerySearchParams()
    const qFrom = query.get('from')
    const qTo = query.get('to')
    const qFromC = query.get('fromc')
    const qToC = query.get('toc')
    const qMerchant = query.get('merchant')
    const qBrands = query.get('brands')
    const qPlatforms = query.get('platforms')
    const qOrderStatus = query.get('status')

    const parseMomentOrNull = (val) => {
        if (!val || val === 'null' || val === 'Invalid date') return null
        const m = moment(val)
        return m.isValid() ? m : null
    }

    const urlStart = parseMomentOrNull(qFrom) || defaultFilters.dateRange.startDate
    const urlEnd = parseMomentOrNull(qTo) || defaultFilters.dateRange.endDate
    const urlStartC = parseMomentOrNull(qFromC) || defaultFilters.dateComparison.startDate
    const urlEndC = parseMomentOrNull(qToC) || defaultFilters.dateComparison.endDate

    useEffect(() => {
        setPaths([{text: t('breadcrumb.home'), link: false, path: '/'}])
    }, [setPaths, t])

    const replaceHistory = useCallback((filters) => {
        const format = 'YYYY-MM-DDTHH:mm:ss.SSSZ'
        const fallbackStart = moment().local().add(-7, 'day').startOf('day')
        const fallbackEnd = moment().local().add(-1, 'day').endOf('day')

        const safeFormat = (val, fb, allowNull = false) => {
            if (val === undefined || val === null) return allowNull ? null : fb.format(format)
            let m
            if (moment.isMoment(val)) m = val
            else if (val instanceof Date) m = moment(val)
            else if (typeof val === 'string') {
                const trimmed = val.trim()
                if (!trimmed || trimmed.toLowerCase() === 'null' || trimmed === 'Invalid date') return allowNull ? null : fb.format(format)
                m = moment(trimmed)
            }
            if (!m || !m.isValid()) return allowNull ? null : fb.format(format)
            return m.format(format)
        }

        const from = safeFormat(filters?.dateRange?.startDate, fallbackStart)
        const to = safeFormat(filters?.dateRange?.endDate, fallbackEnd)
        const fromcRaw = safeFormat(filters?.dateComparison?.startDate, fallbackStart, true)
        const tocRaw = safeFormat(filters?.dateComparison?.endDate, fallbackEnd, true)

        const params = new URLSearchParams()

        if (filters.merchantId) {
            params.append('merchant', filters.merchantId)
        }

        if (filters.brandIds && filters.brandIds.length > 0) {
            params.append('brands', filters.brandIds.join(','))
        }

        if (filters.platformIds && filters.platformIds.length > 0) {
            params.append('platforms', filters.platformIds.join(','))
        }

        if (filters.orderStatus && filters.orderStatus.length > 0) {
            params.append('status', filters.orderStatus.join(','))
        }

        params.append('from', from)
        params.append('to', to)
        params.append('fromc', fromcRaw || 'null')
        params.append('toc', tocRaw || 'null')

        history.replace({pathname: history.location?.pathname || '/analytics', search: params.toString()})
    }, [history])

    const buildBaseDefaultFilters = () => ({
        merchantId: qMerchant || (isProviderAPos(user.provider) ? user.provider.object.id : defaultFilters.merchantId),
        brandIds: qBrands ? qBrands.split(/,/) : defaultFilters.brandIds,
        platformIds: qPlatforms ? qPlatforms.split(/,/) : defaultFilters.platformIds,
        orderStatus: qOrderStatus ? qOrderStatus.split(/,/) : defaultFilters.orderStatus,
        dateRange: {startDate: urlStart, endDate: urlEnd},
        dateComparison: {startDate: urlStartC, endDate: urlEndC},
    })

    const handleFiltersChange = (filters) => replaceHistory(filters)

    // Company specific panes
    const companySpecificPanes = [
        {
            menuItem: {
                as: NavLink,
                id: 'deployments',
                content: t('home.submenus.deployments'),
                to: `/analytics/deployments${history.location.search}`,
                exact: true,
                key: 'deployments',
            },
            pane: (
              <Route
                key="deployments"
                path="/analytics/deployments"
                exact
                render={() => (
                  <Deployments
                    defaultFilters={buildBaseDefaultFilters()}
                    handleFiltersChange={replaceHistory}
                  />
                )}
              />
            ),
        },
        ...(!isEnabled('opening-times') ? [{
            menuItem: {
                as: NavLink,
                id: 'paused',
                content: t('home.submenus.paused'),
                to: `/analytics/paused${history.location.search}`,
                exact: true,
                key: 'paused',
            },
            pane: (
              <Route
                key="paused"
                path="/analytics/paused"
                exact
                render={() => (
                  <PausedStores
                    defaultFilters={buildBaseDefaultFilters()}
                    handleFiltersChange={replaceHistory}
                  />
                )}
              />
            ),
        }] : [{
            menuItem: {
                as: NavLink,
                id: 'opening-activity',
                content: t('home.submenus.paused'),
                to: '/analytics/openings',
                exact: false,
                key: 'opening-activity',
            },
            pane: (
              <Route
                key="opening-activity"
                path="/analytics/openings/opening-activity"
                exact
                render={() => (
                  <OpeningActivity/>
                )}
              />
            ),
        }, {
            pane: (
              <Route
                key="opening-times"
                path="/analytics/openings/opening-times"
                exact
                render={() => (
                  <OpeningTimes/>
                )}
              />
            ),
        }, {
            pane: (
              <Route
                key="current-openings"
                path="/analytics/openings/current-openings"
                exact
                render={() => (
                  <PausedStores
                    defaultFilters={buildBaseDefaultFilters()}
                    handleFiltersChange={replaceHistory}
                  />
                )}
              />
            ),
        }]),
    ]

    // POS specific panes
    const posSpecificPanes = [
        ...(isEnabled('margin')
          ? [
              {
                  menuItem: {
                      as: NavLink,
                      id: 'margin',
                      content: t('home.submenus.margin'),
                      to: `/analytics/margin${history.location.search}`,
                      exact: true,
                      key: 'margin',
                  },
                  pane: (
                    <Route
                      key="margin"
                      path="/analytics/margin"
                      exact
                      render={() => <Margin handleFiltersChange={replaceHistory}/>}
                    />
                  ),
              },
          ]
          : []),
        ...(isEnabled('opening-times') ? [{
            menuItem: {
                as: NavLink,
                id: 'opening-times',
                content: t('home.submenus.paused'),
                to: `/analytics/openings/opening-times`,
                exact: true,
                key: 'opening-times',
            },
            pane: (
              <Route
                key="opening-times"
                path="/analytics/openings/opening-times"
                exact
                render={() => (
                  <OpeningTimes/>
                )}
              />
            ),
        }] : []),
    ]

    const panes = [
        {
            menuItem: {
                as: NavLink,
                id: 'revenues',
                content: t('home.submenus.revenues'),
                to: `/analytics${history.location.search}`,
                exact: true,
                key: 'revenues',
            },
            pane: (
              <Route
                key="revenues"
                path="/analytics"
                exact
                render={() => (
                  <Revenues
                    defaultFilters={buildBaseDefaultFilters()}
                    handleFiltersChange={replaceHistory}
                  />
                )}
              />
            ),
        },
        {
            menuItem: {
                as: NavLink,
                id: 'failures',
                content: t('home.submenus.failures'),
                to: `/analytics/missed${history.location.search}`,
                exact: true,
                key: 'failures',
            },
            pane: (
              <Route
                key="failures"
                path="/analytics/missed"
                exact
                render={() => (
                  <Failures
                    defaultFilters={buildBaseDefaultFilters()}
                    handleFiltersChange={handleFiltersChange}
                  />
                )}
              />
            ),
        },
        {
            menuItem: {
                as: NavLink,
                id: 'unavailable-items',
                content: t('unavailable_items.header'),
                to: '/analytics/unavailable-items',
                exact: true,
                key: 'unavailable-items',
            },
            pane: (
              <Route
                key="unavailable-items"
                path="/analytics/unavailable-items"
                exact
                render={() => <UnavailableItems/>}
              />
            ),
        },
        {
            menuItem: {
                as: NavLink,
                id: 'detailed',
                content: t('home.submenus.detailed'),
                to: `/analytics/detailed${history.location.search}`,
                exact: true,
                key: 'detailed',
            },
            pane: (
              <Route
                key="detailed"
                path="/analytics/detailed"
                exact
                render={() => (
                  <Detailed
                    defaultFilters={buildBaseDefaultFilters()}
                    handleFiltersChange={replaceHistory}
                  />
                )}
              />
            ),
        },
        ...((isProviderACompany(user?.provider) || user.isLoggedInOwnScope) ? companySpecificPanes : posSpecificPanes),
        ...(!user.isLoggedInOwnScope ? [{
            menuItem: {
                as: NavLink,
                id: 'performanceReportConfiguration',
                content: t('reports.receiveAReport'),
                to: '/analytics/reports',
                exact: true,
                key: 'performanceReportConfiguration',
            },
            pane: (
              <Route
                key="performanceReportConfiguration"
                path="/analytics/reports"
                exact
                render={() => <PerformanceReportConfigurationList/>}
              />
            ),
        }] : []),
    ]

    return (
      <Switch>
          <Redirect exact from="/analytics/openings" to="/analytics/openings/opening-activity"/>
          <>
              {isEnabled('dashboard') && (
                <FeatureInfo featureName="dashboard"
                             css={css`
                                 &.ui.message {
                                     margin-bottom: 1rem;
                                 }

                                 &.blue.info.circle.icon {
                                     margin-top: 1rem;
                                     width: 16px;
                                     height: 16px;
                                     vertical-align: text-top;
                                 }
                             `}>
                    <FeatureInfo.Text><strong>{t('home.feature_info.title')}</strong></FeatureInfo.Text>
                    <FeatureInfo.Text>{t('home.feature_info.message')}</FeatureInfo.Text>
                    <div css={css`
                        display: flex;
                        justify-content: flex-end;
                    `}>
                        <Button
                          color="blue"
                          as={Link}
                          to="/dashboard"
                          onClick={() => {
                              setDashboardSoftRolloutTarget(null)
                              track('click_try_dashboard')
                          }}
                        >
                            {t('home.feature_info.try_it_now')}
                            <Icon name='arrow right' />
                        </Button>

                    </div>
                </FeatureInfo>
              )}

              <Tab
                css={css`margin-top: 20px;`}
                renderActiveOnly={false}
                activeIndex={-1}
                panes={panes}
                menu={{secondary: true, pointing: true, className: 'wrapped'}}
              /></>
      </Switch>
    )
}

export default Home
